/**
 * useScanner
 *
 * 「シャッターを押す → カメラからフレーム取得 → Claude API送信 → 結果保持」までを
 * 一括で扱うフック。
 *
 * Phase 1: 手動シャッター（shoot）のみ
 * Phase 2: SCAN モード（startScanning / stopScanning）を追加
 *   - 5秒間隔で低解像度キャプチャを取得
 *   - 前フレームとの差分が閾値（0.08）以下ならAPIをスキップ
 *   - 閾値を超えたらフル解像度キャプチャ → Claude API
 *   - 連続エラー時の自動停止・429時の即停止
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { analyzeImage, ClaudeApiError } from '../lib/claude';
import { captureFrameFromVideo } from '../lib/imageUtils';
import { captureLowResImageData, computeDiff, DEFAULT_DIFF_THRESHOLD } from '../lib/imageDiff';

import type { Mode, ScanResult } from '../types';

export type ScannerStatus = 'idle' | 'processing' | 'done' | 'error';

/** SCAN モードのデフォルト間隔（ms）。設定で上書き可能 */
const DEFAULT_SCAN_INTERVAL_MS = 5000;

/** 連続エラーが何回続いたら自動停止するか */
const MAX_CONSECUTIVE_ERRORS = 3;

export interface UseScannerArgs {
  /** カメラの<video>要素への参照 */
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  /** 認識モード */
  mode: Mode;
  /** APIキー（未設定時は null） */
  apiKey: string | null;
  /** SCAN間隔（ms）。未指定時は5000 */
  scanIntervalMs?: number;
}

export interface UseScannerResult {
  status: ScannerStatus;
  /** 直近のスキャン結果 */
  latestResult: ScanResult | null;
  /** ユーザー向けエラーメッセージ */
  errorMessage: string | null;
  /** 連続認識中かどうか */
  isScanning: boolean;
  /** シャッターを切って解析する */
  shoot: () => Promise<void>;
  /** 連続認識を開始 */
  startScanning: () => void;
  /** 連続認識を停止 */
  stopScanning: () => void;
  /** 結果・エラーをリセット */
  reset: () => void;
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useScanner(args: UseScannerArgs): UseScannerResult {
  const { videoRef, mode, apiKey, scanIntervalMs = DEFAULT_SCAN_INTERVAL_MS } = args;

  const [status, setStatus] = useState<ScannerStatus>('idle');
  const [latestResult, setLatestResult] = useState<ScanResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  // 二重起動防止フラグ（処理中に再度押されたら無視）
  const inFlightRef = useRef(false);

  // SCANモードの内部状態
  const intervalRef = useRef<number | null>(null);
  const prevDiffImageRef = useRef<ImageData | null>(null);
  const consecutiveErrorsRef = useRef(0);

  // 進行中の analyzeImage を中断するための AbortController（v0.3.1+）
  const abortRef = useRef<AbortController | null>(null);

  // ステートを最新に保つためのref（setIntervalのコールバックから古い値を読まないように）
  const apiKeyRef = useRef(apiKey);
  const modeRef = useRef(mode);
  useEffect(() => { apiKeyRef.current = apiKey; }, [apiKey]);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  /**
   * 1回ぶんの解析処理（手動shoot / SCAN tick の共通本体）
   */
  const runOnce = useCallback(async () => {
    if (inFlightRef.current) return;

    const currentApiKey = apiKeyRef.current;
    const currentMode = modeRef.current;
    const video = videoRef.current;

    // 通訳モードでは画像解析しない（v0.3+：別経路 useInterpreter で処理）
    if (currentMode === 'interpret') return;

    if (!currentApiKey) {
      setStatus('error');
      setErrorMessage('APIキーが設定されていません。');
      return;
    }
    if (!video) {
      setStatus('error');
      setErrorMessage('カメラが利用できません。');
      return;
    }

    inFlightRef.current = true;
    setStatus('processing');
    setErrorMessage(null);

    // 進行中リクエストの中断用コントローラを毎回新調
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const frame = captureFrameFromVideo(video);

      const payload = await analyzeImage({
        apiKey: currentApiKey,
        imageBase64: frame.base64,
        mode: currentMode,  // ガード後 VisionMode に narrowing 済み
        signal: ctrl.signal,
      });

      // text はUI側で「短い1行プレビュー」に使われるので、
      // japanese があれば優先、なければ original、最後の砦は rawText。
      const text = payload.japanese ?? payload.original ?? payload.rawText;

      const result: ScanResult = {
        id: makeId(),
        mode: currentMode,
        text,
        timestamp: new Date().toISOString(),
        // 構造化フィールド（パース成功時のみ埋まる）
        ...(payload.original !== undefined ? { original: payload.original } : {}),
        ...(payload.japanese !== undefined ? { japanese: payload.japanese } : {}),
        ...(payload.sourceLang !== undefined ? { sourceLang: payload.sourceLang } : {}),
        ...(payload.confidence !== undefined ? { confidence: payload.confidence } : {}),
      };
      setLatestResult(result);
      setStatus('done');
      consecutiveErrorsRef.current = 0;  // 成功でカウンタリセット
    } catch (err) {
      // STOP ボタン由来の中断は静かに idle 化する（ユーザーに見せるエラーではない）
      if (err instanceof ClaudeApiError && err.status === null && err.message === 'Aborted') {
        setStatus('idle');
        setErrorMessage(null);
        return;
      }

      console.error('[useScanner] runOnce failed:', err);
      const baseMessage =
        err instanceof ClaudeApiError
          ? err.userMessage
          : err instanceof Error
            ? err.message
            : '解析に失敗しました。';

      consecutiveErrorsRef.current += 1;

      // 自動停止が必要かを判定し、メッセージに停止理由を付加する
      const isRateLimit = err instanceof ClaudeApiError && err.status === 429;
      const isMaxErrors = consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS;
      const wasScanning = intervalRef.current !== null;

      let suffix = '';
      if (wasScanning && (isRateLimit || isMaxErrors)) {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsScanning(false);
        prevDiffImageRef.current = null;
        suffix = isRateLimit
          ? '\n（レート制限のため連続認識を自動停止しました）'
          : `\n（${MAX_CONSECUTIVE_ERRORS}回連続でエラーが発生したため連続認識を自動停止しました）`;
      }

      setErrorMessage(baseMessage + suffix);
      setStatus('error');
    } finally {
      inFlightRef.current = false;
      // 自分が登録した controller のときだけクリア（並列shootは想定していないが念のため）
      if (abortRef.current === ctrl) {
        abortRef.current = null;
      }
    }
  }, [videoRef]);

  const shoot = useCallback(async () => {
    // 手動shootは差分検知をスキップ（ユーザーが意図して押している）
    await runOnce();
  }, [runOnce]);

  /**
   * SCAN モードの1ティック処理
   * 差分検知 → 閾値超なら runOnce を呼ぶ
   */
  const tick = useCallback(async () => {
    if (inFlightRef.current) return;
    const video = videoRef.current;
    if (!video) return;

    let imageData: ImageData;
    try {
      imageData = captureLowResImageData(video);
    } catch (err) {
      console.warn('[useScanner] low-res capture failed:', err);
      return;
    }

    const result = computeDiff(prevDiffImageRef.current, imageData, DEFAULT_DIFF_THRESHOLD);
    prevDiffImageRef.current = imageData;

    if (!result.changed) {
      // シーン変化なし → API呼ばずスキップ
      console.debug('[useScanner] skip: diff =', result.diff.toFixed(4));
      return;
    }

    console.debug('[useScanner] tick: diff =', result.diff.toFixed(4), '→ analyze');
    await runOnce();
  }, [runOnce, videoRef]);

  const startScanning = useCallback(() => {
    if (intervalRef.current !== null) return;
    consecutiveErrorsRef.current = 0;
    prevDiffImageRef.current = null;
    setIsScanning(true);
    // 開始直後にも1回走らせる（待ち時間が体感悪いので）
    void tick();
    intervalRef.current = window.setInterval(() => {
      void tick();
    }, scanIntervalMs);
  }, [tick, scanIntervalMs]);

  /**
   * SCANを完全に停止（v0.3.1+）：
   *   - インターバルを止める
   *   - 進行中の API 呼び出しがあれば AbortController で中断
   *   - status を 'idle' に戻す（ANALYZING… → STAND BY 表示）
   *   - 結果モーダル（latestResult）はクリアしない（× で閉じるまで残す）
   */
  const stopScanning = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    inFlightRef.current = false;
    setIsScanning(false);
    prevDiffImageRef.current = null;
    setStatus('idle');
    setErrorMessage(null);
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setLatestResult(null);
    setErrorMessage(null);
  }, []);

  // モード変更時：前フレーム差分情報をリセット（モード切替直後は必ず1回解析させる）
  useEffect(() => {
    prevDiffImageRef.current = null;
  }, [mode]);

  // アンマウント時にinterval停止
  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    status,
    latestResult,
    errorMessage,
    isScanning,
    shoot,
    startScanning,
    stopScanning,
    reset,
  };
}
